import graphqlClient from '@/graphql/graphql-client';
import { eqFromWhere, organizationIdForQuery, toListQueryVariables } from '@/graphql/list-query';
import type { ListQueryVariables } from '@/graphql/types';
import { useQuery } from '@tanstack/react-query';
import {
  TemplatessAvailableDocument,
  TemplatessByItemIdDocument,
  TemplatessBySlugDocument,
  TemplatessDocument,
  type TemplatessQuery,
} from '@/graphql/generated/graphql';

export type TemplateRecord = NonNullable<
  NonNullable<TemplatessQuery['getTemplatess']>['items']
>[number];
export type GetTemplatesResponse = TemplatessQuery;

const isAvailableWhere = (where: unknown) => {
  if (!where || typeof where !== 'object') return false;
  const w = where as Record<string, unknown>;
  if (w.Available && typeof w.Available === 'object' && (w.Available as { eq?: boolean }).eq === true) {
    return true;
  }
  if (Array.isArray(w.and)) return w.and.some(isAvailableWhere);
  return false;
};

export const toTemplatesRequest = (params?: ListQueryVariables<Record<string, unknown>> | null) => {
  const { where, paging } = toListQueryVariables(params);
  const pageNo = paging.pageNo;
  const pageSize = paging.pageSize;
  const itemId = eqFromWhere(where, 'ItemId');
  const slug = eqFromWhere(where, 'Slug');
  const organizationId = organizationIdForQuery(where);

  if (itemId) {
    return { document: TemplatessByItemIdDocument, variables: { itemId, pageNo, pageSize } };
  }
  if (slug) {
    return { document: TemplatessBySlugDocument, variables: { slug, pageNo, pageSize } };
  }
  if (isAvailableWhere(where)) {
    return {
      document: TemplatessAvailableDocument,
      variables: { organizationId, pageNo, pageSize },
    };
  }
  return { document: TemplatessDocument, variables: { organizationId, pageNo, pageSize } };
};

export const requestTemplates = (params?: ListQueryVariables<Record<string, unknown>> | null) => {
  const { document, variables } = toTemplatesRequest(params);
  return graphqlClient.request(document, variables);
};

export const useGetTemplates = (params?: ListQueryVariables<Record<string, unknown>>) => {
  const request = toTemplatesRequest(params);
  return useQuery({
    queryKey: ['templates', request.variables],
    queryFn: () => graphqlClient.request(request.document, request.variables),
  });
};

export const GET_TEMPLATES_QUERY = TemplatessDocument;
export const GET_TEMPLATES_BY_SLUG_QUERY = TemplatessBySlugDocument;
export const GET_TEMPLATES_BY_ITEM_ID_QUERY = TemplatessByItemIdDocument;
export const GET_TEMPLATES_AVAILABLE_QUERY = TemplatessAvailableDocument;
